import React, { useState } from 'react';
import ChartModal from './ChartModal';

interface ChartFrameProps {
  title: string;
  showRaw: boolean;
  onToggle: (showRaw: boolean) => void;
  /** When true the chart view is unavailable (e.g. a cyclic Sankey) — data only. */
  chartDisabled?: boolean;
  /** Renders the chart. `fullscreen` is true inside the expand modal. */
  renderChart: (fullscreen: boolean) => React.ReactNode;
  renderTable: () => React.ReactNode;
}

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

/**
 * Shared shell for every AI chat chart: the title bar, the Chart/Data toggle,
 * a mobile-only "Expand" button (charts are too small inside a bubble on a
 * phone), and the fullscreen modal. Individual charts only supply how to draw
 * themselves and their data table.
 */
const ChartFrame: React.FC<ChartFrameProps> = ({
  title,
  showRaw,
  onToggle,
  chartDisabled,
  renderChart,
  renderTable,
}) => {
  const [expanded, setExpanded] = useState(false);
  const showingChart = !showRaw && !chartDisabled;

  return (
    <div className="chart-wrapper">
      <div className="chart-toggle-row">
        <span className="chart-title">{title}</span>
        <div className="chart-toggle-actions">
          <div className="chart-toggle">
            <button className={showingChart ? 'active' : ''} disabled={chartDisabled} onClick={() => onToggle(false)}>Chart</button>
            <button className={!showingChart ? 'active' : ''} onClick={() => onToggle(true)}>Data</button>
          </div>
          {!chartDisabled && (
            <button
              type="button"
              className="chart-expand-btn"
              onClick={() => setExpanded(true)}
              aria-label={`Expand ${title} chart to fullscreen`}
              title="Expand chart"
            >
              <ExpandIcon />
            </button>
          )}
        </div>
      </div>

      {showingChart ? (
        <div className="chart-view">{renderChart(false)}</div>
      ) : (
        renderTable()
      )}

      {expanded && (
        <ChartModal title={title} onClose={() => setExpanded(false)}>
          {renderChart(true)}
        </ChartModal>
      )}
    </div>
  );
};

export default ChartFrame;
